export const CURRENT_MAP_CONFIG_VERSION = 1;
export const DEFAULT_LABEL_FONT_SIZE = 12;

export interface MapImageConfig {
  x: number;
  y: number;
  rotation: number;
  opacity: number;
  locked: boolean;
}

export interface MapConfig {
  image: MapImageConfig;
  labelFontSize: number;
}

interface MapConfigEnvelope {
  version: number;
  config: MapConfig;
}

export const DEFAULT_MAP_CONFIG: MapConfig = {
  image: { x: 0, y: 0, rotation: 0, opacity: 0.3, locked: true },
  labelFontSize: DEFAULT_LABEL_FONT_SIZE,
};

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

const normalizeOpacity = (value: number): number => Math.min(1, Math.max(0, value));
const normalizeLabelFontSize = (value: unknown): number => (isNumber(value) && value > 0 ? value : DEFAULT_LABEL_FONT_SIZE);

const normalizeConfig = (config: MapConfig): MapConfig => ({
  image: {
    x: config.image.x,
    y: config.image.y,
    rotation: config.image.rotation,
    opacity: normalizeOpacity(config.image.opacity),
    locked: config.image.locked,
  },
  labelFontSize: normalizeLabelFontSize(config.labelFontSize),
});

const isEnvelope = (value: unknown): value is MapConfigEnvelope => {
  if (!value || typeof value !== 'object') return false;
  const envelope = value as Record<string, unknown>;
  if (!isNumber(envelope.version) || !envelope.config || typeof envelope.config !== 'object') return false;

  const config = envelope.config as Record<string, unknown>;
  if (!config.image || typeof config.image !== 'object') return false;

  const image = config.image as Record<string, unknown>;
  return isNumber(image.x) && isNumber(image.y) && isNumber(image.rotation) && isNumber(image.opacity) && isBoolean(image.locked);
};

export const serializeMapConfig = (config: MapConfig): string =>
  JSON.stringify({ version: CURRENT_MAP_CONFIG_VERSION, config: normalizeConfig(config) } satisfies MapConfigEnvelope);

export const deserializeMapConfig = (raw: string | null | undefined): MapConfig => {
  if (!raw) return DEFAULT_MAP_CONFIG;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isEnvelope(parsed)) {
      // eslint-disable-next-line no-console
      console.warn('[WarehouseMap] mapConfig envelope invalid, using default');
      return DEFAULT_MAP_CONFIG;
    }

    return normalizeConfig(parsed.config);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[WarehouseMap] mapConfig JSON invalid, using default', error);
    return DEFAULT_MAP_CONFIG;
  }
};
